/**
 * Accountant Reconciliation Page
 * ================================
 * Forensic accounting interface for P&L reconciliation.
 *
 * Three modes:
 * 1. Year-at-a-glance P&L grid (default)
 * 2. Cell drill-down reconciliation view
 * 3. Multi-source comparison (bank statements vs credit cards vs tax returns)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import { useUser } from '../contexts/TestAuthContext';
import AccountantLayout from '../components/AccountantLayout';
import PLReconciliationGrid from '../components/accountant/PLReconciliationGrid';
import ReconciliationPanel from '../components/accountant/ReconciliationPanel';
import MultiSourceReconciliation from '../components/accountant/MultiSourceReconciliation';
import ReconciliationReviewPanel from '../components/accountant/ReconciliationReviewPanel';
import PayrollAdjustmentForm from '../components/accountant/PayrollAdjustmentForm';
import api from '../utils/api';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  LayoutGrid,
  GitBranch,
  Users,
} from 'lucide-react';

type ViewMode = 'grid' | 'multi-source' | 'merchant-review' | 'payroll-addbacks';

interface PLGridData {
  report_id: string;
  years: number[];
  pl_structure: any;
  pl_grid: any;
  reconciliation_status: Record<string, string>;
  pattern_event_id?: string | null;
  catch_all_items: Array<[string, string]>;
  data_source?: 'transactions' | 'pl_summary' | 'none';
  drill_down_available?: boolean;
}

export default function AccountantReconciliation() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accountant, loading: authLoading } = useAccountantAuth();
  // user lives on useUser(), not useAuth() — see AttorneyQueue note.
  const { user } = useUser();

  // In test mode, bypass accountant auth requirement
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

  // Memoize the test accountant to prevent re-render loops
  const testAccountant = React.useMemo(() => ({
    id: 'test_user_123',
    email: 'test@claimsync.com',
    full_name: 'Test Accountant'
  }), []);

  // Support Clerk-authed users coming from AccountantQueue.
  // Defensive fallback chain that supports both Clerk's user shape and
  // the test-mode mock shape. Cast to any per archaeology §7.
  const clerkAccountant = React.useMemo(() => user ? ({
    id: (user as any).sub || (user as any).userId || user.id || 'clerk-user',
    email: (user as any).email || (user as any).primaryEmailAddress?.emailAddress || '',
    full_name: (user as any).name || user.fullName || (user as any).email || 'Staff'
  }) : null, [user]);

  const effectiveAccountant = isTestMode ? testAccountant : (accountant || clerkAccountant);
  const effectiveAuthLoading = isTestMode ? false : (authLoading && !user);

  const [plGridData, setPlGridData] = useState<PLGridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // View mode: grid (monthly drill-down) or multi-source (source comparison)
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Drill-down state
  const [selectedCell, setSelectedCell] = useState<{
    year: number;
    month: number;
    section: string;
    line_item: string;
  } | null>(null);

  useEffect(() => {
    // Wait for auth context to finish loading
    if (effectiveAuthLoading) {
      return;
    }
    
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }

    if (!reportId) {
      setError('No report ID provided');
      setLoading(false);
      return;
    }

    fetchPLGrid();
  }, [reportId, effectiveAccountant, effectiveAuthLoading]);

  // Check URL params for drill-down
  useEffect(() => {
    const year = searchParams.get('year');
    const month = searchParams.get('month');
    const section = searchParams.get('section');
    const lineItem = searchParams.get('line_item');

    if (year && month && section && lineItem) {
      setSelectedCell({
        year: parseInt(year),
        month: parseInt(month),
        section: decodeURIComponent(section),
        line_item: decodeURIComponent(lineItem)
      });
    }
  }, [searchParams]);

  const fetchPLGrid = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching P&L grid for report:', reportId);
      // api.get returns data directly, not wrapped in response object
      const data = await api.get(`/api/v1/reconciliation/pl-grid/${reportId}`, { timeoutMs: 60000 });
      console.log('P&L grid response:', data);
      setPlGridData(data);
    } catch (err: any) {
      console.error('Error fetching P&L grid:', err);
      setError(
        err.name === 'AbortError'
          ? 'This is taking longer than expected — please try again.'
          : err.message || 'Failed to load P&L grid'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (year: number, month: number, section: string, lineItem: string) => {
    // Check if drill-down is available
    if (plGridData && plGridData.data_source !== 'transactions') {
      // Show info message for P&L summary data
      setInfoMessage('This report was created using P&L summary data. Transaction-level drill-down is not available, but you can still view the aggregated amounts in the grid.');
      return;
    }
    
    // Update URL params
    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
      section: section,
      line_item: lineItem
    });
    navigate(`/accountant/reconciliation/${reportId}?${params.toString()}`);

    setSelectedCell({ year, month, section, line_item: lineItem });
  };

  const handleBackToGrid = () => {
    navigate(`/accountant/reconciliation/${reportId}`);
    setSelectedCell(null);
  };


  if (loading) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center h-screen" style={{ background: 'var(--color-bg-base)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 mx-auto mb-4" style={{ borderBottom: '2px solid var(--color-brand)' }}></div>
            <p className="cs-meta">Loading P&L reconciliation...</p>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (error) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center h-screen" style={{ background: 'var(--color-bg-base)' }}>
          <div className="p-6 max-w-md" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14 }}>
            <h2 className="cs-h2 mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Error</h2>
            <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
            <button
              onClick={() => navigate(reportId ? `/accountant/reports/${reportId}` : '/accountant/reports')}
              className="cs-body mt-4 px-4 py-2 rounded"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Back to Report
            </button>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  if (!plGridData) {
    return null;
  }

  // Check if this is P&L summary data (no transaction drill-down)
  const isPLSummary = plGridData.data_source === 'pl_summary' || plGridData.drill_down_available === false;
  const hasNoData = plGridData.data_source === 'none';

  return (
    <AccountantLayout>
      <div className="container mx-auto px-4 py-6 max-w-7xl" style={{ background: 'var(--color-bg-base)' }}>
        {/* P&L Summary Banner */}
        {isPLSummary && !hasNoData && (
          <div className="p-4 mb-6 flex items-start gap-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14 }}>
            <div>
              <h3 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>P&L Summary View</h3>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                This report was created from P&L summary data. The grid shows monthly aggregates,
                but individual transaction drill-down is not available. To enable transaction-level
                reconciliation, bank statement data would need to be uploaded.
              </p>
            </div>
          </div>
        )}

        {/* No Data Banner */}
        {hasNoData && (
          <div className="p-4 mb-6 flex items-start gap-3" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div>
              <h3 className="cs-h3">No Financial Data</h3>
              <p className="cs-body">
                This report does not contain financial data for reconciliation.
                Please ensure P&L statements or bank statements have been uploaded.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {selectedCell && (
                <button
                  onClick={handleBackToGrid}
                  className="p-2 rounded-lg transition-colors"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  title="Back to P&L Grid"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div>
                <h1 className="cs-display mb-1 flex items-center">
                  {selectedCell
                    ? `Reconciliation: ${selectedCell.line_item}`
                    : viewMode === 'multi-source'
                    ? 'P&L Source Comparison'
                    : 'P&L Reconciliation'}
                  {isPLSummary && !selectedCell && viewMode === 'grid' && (
                    <span className="cs-chip ml-2 px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      Summary View
                    </span>
                  )}
                </h1>
                <p className="cs-meta">
                  {selectedCell
                    ? `${new Date(selectedCell.year, selectedCell.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} • ${selectedCell.section}`
                    : viewMode === 'multi-source'
                    ? 'Reconstruct P&L from bank statements, tax returns, and credit card statements'
                    : `Report ID: ${reportId?.substring(0, 8)}...`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              {!selectedCell && (
                <div className="flex rounded-lg p-1" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                  <button
                    onClick={() => setViewMode('grid')}
                    className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
                    style={viewMode === 'grid'
                      ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { background: 'transparent', color: 'var(--color-text-muted)' }}
                    title="Monthly P&L Grid"
                  >
                    <LayoutGrid size={16} />
                    Monthly Grid
                  </button>
                  <button
                    onClick={() => setViewMode('multi-source')}
                    className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
                    style={viewMode === 'multi-source'
                      ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { background: 'transparent', color: 'var(--color-text-muted)' }}
                    title="Compare sources: Bank, Credit Card, Tax Returns"
                  >
                    <ArrowLeftRight size={16} />
                    Source Comparison
                  </button>
                  <button
                    onClick={() => setViewMode('merchant-review')}
                    className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
                    style={viewMode === 'merchant-review'
                      ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { background: 'transparent', color: 'var(--color-text-muted)' }}
                    title="Review + override merchant ACH matches"
                    data-testid="tab-merchant-review"
                  >
                    <GitBranch size={16} />
                    Merchant Review
                  </button>
                  <button
                    onClick={() => setViewMode('payroll-addbacks')}
                    className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
                    style={viewMode === 'payroll-addbacks'
                      ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                      : { background: 'transparent', color: 'var(--color-text-muted)' }}
                    title="Enter payroll add-backs (officer comp, non-continuing wages)"
                    data-testid="tab-payroll-addbacks"
                  >
                    <Users size={16} />
                    Payroll Add-Backs
                  </button>
                </div>
              )}

              <button
                onClick={() => navigate(`/accountant/case/${reportId}/export`)}
                className="cs-chip flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                data-testid="open-export-center"
              >
                Go to Export Center
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {selectedCell ? (
          <ReconciliationPanel
            reportId={reportId!}
            year={selectedCell.year}
            month={selectedCell.month}
            section={selectedCell.section}
            lineItem={selectedCell.line_item}
            eventId={plGridData?.pattern_event_id ?? undefined}
            onBack={handleBackToGrid}
          />
        ) : viewMode === 'multi-source' ? (
          <MultiSourceReconciliation
            reportId={reportId!}
          />
        ) : viewMode === 'merchant-review' ? (
          <ReconciliationReviewPanel reportId={reportId!} />
        ) : viewMode === 'payroll-addbacks' ? (
          <PayrollAdjustmentForm reportId={reportId!} />
        ) : (
          <PLReconciliationGrid
            plGridData={plGridData}
            onCellClick={handleCellClick}
          />
        )}
      </div>
    </AccountantLayout>
  );
}
